import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import divisionService from "../../services/DivisionService";

// ── Thunks ───────────────────────────────────────────────────────────────────
export const getDivisionList = createAsyncThunk(
  "division/GetDivisions",
  async (_, { rejectWithValue }) => {
    try {
      const response = await divisionService.GetDivisions();
      return response.data?.ResultSet || [];
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.Message ||
          error.message ||
          "Failed to get division list"
      );
    }
  }
);

export const getDivisionById = createAsyncThunk(
  "division/GetDivisionById",
  async (p_div_code, { rejectWithValue }) => {
    try {
      const response = await divisionService.GetDivisionById(p_div_code);
      return response.data?.ResultSet || [];
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.Message ||
          error.message ||
          "Failed to get division"
      );
    }
  }
);

export const addDivision = createAsyncThunk(
  "division/AddDivision",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await divisionService.AddDivision(payload);
      return response.data?.ResultSet || [];
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.Message ||
          error.message ||
          "Failed to add division"
      );
    }
  }
);

export const updateDivision = createAsyncThunk(
  "division/UpdateDivision",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await divisionService.UpdateDivision(payload);
      return response.data?.ResultSet || [];
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.Message ||
          error.message ||
          "Failed to update division"
      );
    }
  }
);

// ── Slice ────────────────────────────────────────────────────────────────────
const initialState = {
  divisions: [],
  selectedDivision: null,
  loading: false,
  saving: false,
  error: null,
  success: false,
};

const divisionSlice = createSlice({
  name: "division",
  initialState,
  reducers: {
    clearDivisionError: (state) => {
      state.error = null;
    },
    clearDivisionSuccess: (state) => {
      state.success = false;
    },
    clearDivisions: (state) => {
      state.divisions = [];
      state.selectedDivision = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Get list
      .addCase(getDivisionList.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getDivisionList.fulfilled, (state, action) => {
        state.loading = false;
        state.divisions = action.payload;
      })
      .addCase(getDivisionList.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to load divisions";
      })

      // Get by id
      .addCase(getDivisionById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getDivisionById.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedDivision = action.payload?.[0] || null;
      })
      .addCase(getDivisionById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to load division";
      })

      // Add
      .addCase(addDivision.pending, (state) => {
        state.saving = true;
        state.error = null;
        state.success = false;
      })
      .addCase(addDivision.fulfilled, (state) => {
        state.saving = false;
        state.success = true;
      })
      .addCase(addDivision.rejected, (state, action) => {
        state.saving = false;
        state.error = action.payload || "Failed to add division";
      })

      // Update
      .addCase(updateDivision.pending, (state) => {
        state.saving = true;
        state.error = null;
        state.success = false;
      })
      .addCase(updateDivision.fulfilled, (state) => {
        state.saving = false;
        state.success = true;
      })
      .addCase(updateDivision.rejected, (state, action) => {
        state.saving = false;
        state.error = action.payload || "Failed to update division";
      });
  },
});

export const {
  clearDivisionError,
  clearDivisionSuccess,
  clearDivisions,
} = divisionSlice.actions;

export default divisionSlice.reducer;